import { defineStore } from 'pinia';
import { ref } from 'vue';
import { gql } from '@apollo/client/core';
import { apolloClient } from '@/plugins/nhost';
import { useClientStore } from './client';

const GET_USER_PERMISSIONS = gql`
  query getUserPermissions($client_id: uuid!) {
    general_view_user_client_permission(where: { client_id: { _eq: $client_id } }) {
      resource
      action
      permission_id
    }
  }
`;

export const usePermissionStore = defineStore('permission', () => {
  const permissions = ref<string[]>([]);
  const availablePermissions = ref<string[]>([]);
  const clientStore = useClientStore();

  function hasPermission(permission: string): boolean {
    if (!permission || typeof permission !== 'string') return false;
    return permissions.value.includes(permission);
  }

  /**
   * TECH-2671 — a personal account (`general.client.kind = 'personal'`) is the person themselves,
   * invited to follow someone else's portfolio. It has no data, no users and no modules of its own,
   * so its effective permissions are an **allow-list** of `portfolio.*` rather than the usual
   * deny-list: a permission added to another area later stays denied here until somebody widens
   * this list on purpose.
   *
   * The backend grants the account's owner role exactly `portfolio.read` (PersonalAccountService),
   * so this is a second, independent gate rather than the only one.
   */
  function isPersonalAccountPermission(permission: string): boolean {
    return permission === 'portfolio.read' || permission.startsWith('portfolio.');
  }

  function getClientMetadataDisabledPermissions() {
    const metadata = clientStore.selectedClient?.metadata;
    if (!metadata) return [];

    const disabledPermissions: string[] = [];

    // Use the same logic as in the component
    if (metadata.cockpitModuleDisabled ?? false) disabledPermissions.push('cockpit.read');
    if (metadata.financialModuleDisabled ?? false) disabledPermissions.push('cockpit.analysis.financial.read');
    if (!(metadata.salesModuleEnabled ?? false)) disabledPermissions.push('cockpit.analysis.sales.read');
    // Reports é opt-out (TECH-2593): só desliga com `reportsModuleDisabled: true`. O antigo
    // `reportsModuleEnabled` deixou de ser lido de propósito — ele já existe como `false` no
    // metadata de praticamente toda empresa (gravado no create e a cada save do backoffice), então
    // enquanto ele fosse consultado o default "ligado" nunca chegava a valer.
    if (metadata.reportsModuleDisabled ?? false) disabledPermissions.push('cockpit.analysis.reports.read');
    if (metadata.operationsModuleDisabled ?? false) disabledPermissions.push('cockpit.analysis.operations.read');
    if (!(metadata.fundingModuleEnabled ?? true)) disabledPermissions.push('cockpit.analysis.funding.read');
    if (!(metadata.enableHubCountAccountingModuleEmbed ?? false))
      disabledPermissions.push('cockpit.analysis.accounting.read');
    if (metadata.indicatorsModuleDisabled ?? false) disabledPermissions.push('cockpit.analysis.indicators.read');
    if (!metadata.enableDataroom) disabledPermissions.push('dataroom.read');
    if (!metadata.enableCompanyArea) disabledPermissions.push('company-area.read');
    // TECH-2599. Default-off: `diagnostico.read` is granted to every admin/owner/viewer role by
    // migration 1787100000001, so the metadata flag — not the permission — is what holds the
    // feature back until someone enables it per client in the backoffice.
    if (!metadata.enableDiagnostico) disabledPermissions.push('diagnostico.read');

    return disabledPermissions;
  }

  async function fetchUserPermissions() {
    if (!clientStore.selectedClient?.id) {
      // TECH-2693 — nobody reaches the app without a client any more: a company-user invite creates
      // the person's own personal account, whose owner role carries portfolio.read like any other.
      return;
    }

    const { data } = await apolloClient.query({
      query: GET_USER_PERMISSIONS,
      variables: {
        client_id: clientStore.selectedClient.id,
      },
    });

    // Use only the permission_id field for permissions
    const rolePermissions =
      data?.general_view_user_client_permission?.map((p: { permission_id: string }) => p.permission_id) || [];

    // A personal account never reaches the metadata deny-list below: whatever its role happens to
    // carry, only portfolio.* survives.
    if (clientStore.selectedClient?.kind === 'personal') {
      permissions.value = rolePermissions.filter(isPersonalAccountPermission);
      return;
    }

    const disabledPermissions = getClientMetadataDisabledPermissions();
    // Filter out all disabled permissions directly from metadata
    let filteredPermissions = rolePermissions.filter((permission: string) => !disabledPermissions.includes(permission));

    // Disable all dependent permissions for each disabled permission from metadata
    disabledPermissions.forEach(disabled => {
      const base = disabled.replace(/\.[^.]+$/, ''); // Remove last segment
      filteredPermissions = filteredPermissions.filter(
        (permission: string) => !permission.startsWith(base + '.') || permission === disabled
      );
    });

    // Deduplicate permissions using Set and ensure type safety
    permissions.value = Array.from(new Set(filteredPermissions)) as string[];
  }

  return {
    permissions,
    availablePermissions,
    fetchUserPermissions,
    hasPermission,
  };
});
